/**
 * CR-6440: customer-view section body for the Purchasing → Forecast filter panel.
 *
 * Sections: Date, Company, Supplier, Classification, Forecast Details, with the
 * cascade logic matching PO Headers:
 *   - Division → Site, Company Country → Site, Customer → Site
 *   - Site → Supplier, Supplier Country → Supplier, Supplier Group → Supplier
 *   - Commodity Group → Commodity Type
 *
 * CR-6448: the supplier view lives in `ForecastFilterSupplierLayout`, and the
 * panel shell (header actions, Clear All / Apply Filters footer) is rendered
 * once by `ForecastFiltersPanel` for both views, so this component is the
 * customer body only.
 *
 * CR-6440 rework (client re-test 30 Sep 2026): the body now uses the same
 * shaded section headers, icons, inset dividers and field styling as the PO
 * Lines customer panel, and carries the diagram's controls in the diagram's
 * order — Date: Import Date · Company: Customer, Country, Division, Site,
 * Warehouse · Supplier: Country, Group, Site, Supplier Code · Classification ·
 * Forecast Details. The Date From / Date To pair (a client-side column window
 * the diagram does not show) is no longer rendered; its state keys remain so
 * nothing else has to change.
 *
 * Tenant shape (Admin Setup → Platform → Filter Visibility, CR-4802) gates
 * Country, Division, Warehouse, Supplier Code and the Classification group,
 * exactly as it does on PO Lines. Input types are preserved as the ticket
 * requires.
 */
import { useMemo } from 'react';
import { Calendar, Building2, Truck, Tags, FileText } from 'lucide-react';
import SearchableSelect from '../../../ui/SearchableSelect';
import { SectionDivider } from '../../../filters/FilterSection';
import { FilterSection, FIELD_CLS, LABEL_CLS } from '../po-lines/filters/fieldStyles';
import { useForecastFilterCascades, ForecastCascadeFilterOptions } from './useForecastFilterCascades';
import { useFilterVisibility } from '../../../../hooks/useFilterVisibility';

interface Option {
  value: string;
  label: string;
}

interface ForecastFilterOptionsShape {
  commodity_groups?: string[];
  commodity_types?: string[];
  supplier_groups?: Array<{ id?: number; name: string }>;
  suppliers?: Array<{ id: number; name: string }>;
  customers?: Array<{ id: number; name: string }>;
  /** CR-6448 / CR-6440 rework: warehouses in scope (value = code). */
  warehouses?: Array<{ code: string; name: string }>;
}

export interface ForecastFilterSectionsProps {
  filters: Record<string, string>;
  onFilterChange: (key: string, value: string) => void;
  filterOptions?: ForecastFilterOptionsShape | null;
  /** Customer portal — supplier pickers only make sense there. */
  isCustomer: boolean;
  /** Schedule-tenant installs carry a separate supplier part number. */
  isScheduleTenant: boolean;
  /** CR-6440: Division options for cascade filtering */
  divisionOptions?: Option[];
  /** CR-6440: Site country options for cascade filtering */
  siteCountries?: string[];
  /** CR-6440: Supplier country options for cascade filtering */
  supplierCountries?: string[];
  /** CR-6440: Site options for cascade filtering */
  siteOptions?: Option[];
  /**
   * CR-6440: Raw filter data for cascade filtering.
   * When provided, cascade logic filters dependent dropdowns automatically.
   */
  rawCascadeOptions?: ForecastCascadeFilterOptions | null;
}

export function ForecastFilterSections({
  filters,
  onFilterChange,
  filterOptions,
  isCustomer,
  isScheduleTenant,
  divisionOptions = [],
  siteCountries = [],
  supplierCountries = [],
  siteOptions: propSiteOptions = [],
  rawCascadeOptions,
}: ForecastFilterSectionsProps) {
  // CR-4802: filters this tenant shows — one answer for every panel.
  const fv = useFilterVisibility();
  // CR-6440: Build filters object for cascade hook (cast from Record<string, string>)
  const typedFilters = {
    partNumber: filters['partNumber'] || '',
    poNumber: filters['poNumber'] || '',
    supplierPartNumber: filters['supplierPartNumber'] || '',
    partSearch: filters['partSearch'] || '',
    commodityGroup: filters['commodityGroup'] || '',
    commodityType: filters['commodityType'] || '',
    supplierGroup: filters['supplierGroup'] || '',
    supplierId: filters['supplierId'] || '',
    customerId: filters['customerId'] || '',
    recordType: filters['recordType'] || '',
    importDateFrom: filters['importDateFrom'] || '',
    importDateTo: filters['importDateTo'] || '',
    dateFrom: filters['dateFrom'] || '',
    dateTo: filters['dateTo'] || '',
    division: filters['division'] || '',
    companyCountry: filters['companyCountry'] || '',
    site: filters['site'] || '',
    supplierCountry: filters['supplierCountry'] || '',
  };

  // CR-6440: Use cascade hook for dependent filter logic
  const {
    siteOptions: cascadedSiteOptions,
    supplierSiteOptions: cascadedSupplierOptions,
    commodityTypeOptions: cascadedCommodityTypes,
    handleDivisionChange,
    handleCompanyCountryChange,
    handleSiteChange,
    handleSupplierCountryChange,
    handleSupplierGroupChange,
    handleCommodityGroupChange,
  } = useForecastFilterCascades(typedFilters, rawCascadeOptions ?? null, onFilterChange);

  // CR-6440: Use cascaded options when raw data is available,
  // otherwise fall back to props for backward compatibility.
  const customerOptions = useMemo<Option[]>(() => {
    if (rawCascadeOptions && rawCascadeOptions.sites.length > 0) {
      return cascadedSiteOptions.map((s) => ({ value: String(s.id), label: s.name }));
    }
    return propSiteOptions;
  }, [rawCascadeOptions, cascadedSiteOptions, propSiteOptions]);

  // Customer → Site: in Helix a customer site is a company record, so the two
  // pickers draw from one list; choosing a Customer narrows Site to it.
  const effectiveSiteOptions = useMemo<Option[]>(() => {
    const customerId = filters['customerId'];
    return customerId ? customerOptions.filter((o) => o.value === customerId) : customerOptions;
  }, [customerOptions, filters]);

  const effectiveSupplierOptions = useMemo<Option[]>(() => {
    if (rawCascadeOptions && rawCascadeOptions.suppliers.length > 0) {
      return cascadedSupplierOptions.map((s) => ({ value: String(s.id), label: s.name }));
    }
    return (filterOptions?.suppliers || []).map((s) => ({ value: String(s.id), label: s.name }));
  }, [rawCascadeOptions, cascadedSupplierOptions, filterOptions?.suppliers]);

  const effectiveCommodityTypes = useMemo<string[]>(() => {
    if (rawCascadeOptions && rawCascadeOptions.commodityTypes.length > 0) {
      return cascadedCommodityTypes.map((t) => t.name);
    }
    return filterOptions?.commodity_types || [];
  }, [rawCascadeOptions, cascadedCommodityTypes, filterOptions?.commodity_types]);

  const supplierGroupOptions = useMemo<Option[]>(
    () => (filterOptions?.supplier_groups || []).map((g) => ({ value: g.name, label: g.name })),
    [filterOptions?.supplier_groups],
  );
  const warehouseOptions = useMemo<Option[]>(
    () => (filterOptions?.warehouses || []).map((w) => ({ value: w.code, label: w.name })),
    [filterOptions?.warehouses],
  );
  const toOptions = (values: string[]): Option[] => values.map((v) => ({ value: v, label: v }));

  const handleCustomerChange = (value: string) => {
    onFilterChange('customerId', value);
    if (value && filters['site'] && filters['site'] !== value) onFilterChange('site', '');
  };

  /** Reusable text input field — same control styling as PO Lines. */
  const textField = (label: string, key: string, placeholder: string) => (
    <div>
      <label className={LABEL_CLS}>{label}</label>
      <input
        type="text"
        value={filters[key] || ''}
        onChange={(e) => onFilterChange(key, e.target.value)}
        placeholder={placeholder}
        className={FIELD_CLS}
      />
    </div>
  );

  /** Reusable native select — input type preserved, styling matches PO Lines. */
  const selectField = (
    label: string,
    key: string,
    options: Option[],
    placeholder: string,
    onChange: (value: string) => void = (value) => onFilterChange(key, value),
  ) => (
    <div>
      <label className={LABEL_CLS}>{label}</label>
      <select value={filters[key] || ''} onChange={(e) => onChange(e.target.value)} className={FIELD_CLS}>
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );

  /** Reusable searchable select — the control the Site pickers already used. */
  const searchableField = (
    label: string,
    key: string,
    options: Option[],
    placeholder: string,
    onChange: (value: string) => void = (value) => onFilterChange(key, value),
  ) => (
    <div>
      <label className={LABEL_CLS}>{label}</label>
      <SearchableSelect options={options} value={filters[key] || ''} onChange={onChange} placeholder={placeholder} />
    </div>
  );

  return (
    <div className="[&>section]:border-t-0">
      <SectionDivider />

      {/* Date — which import snapshot the grid shows (diagram: single Import Date) */}
      <FilterSection title="Date" icon={Calendar}>
        <div>
          <label className={LABEL_CLS}>Import Date</label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="date"
              value={filters['importDate'] || ''}
              onChange={(e) => onFilterChange('importDate', e.target.value)}
              className={`${FIELD_CLS} pl-9`}
            />
          </div>
        </div>
      </FilterSection>

      {isCustomer && (
        <>
          <SectionDivider />

          {/* Company — Customer, Country, Division, Site, Warehouse (cascades: Division/Country/Customer → Site) */}
          <FilterSection title="Company" icon={Building2}>
            {searchableField('Customer', 'customerId', customerOptions, 'All Customers', handleCustomerChange)}
            {/* Country — CR-4802: only when the tenant spans countries */}
            {fv.companyCountry && selectField('Country', 'companyCountry', toOptions(siteCountries), 'All Countries', handleCompanyCountryChange)}
            {/* Division — CR-4802: only when the hierarchy has a division layer */}
            {fv.division && selectField('Division', 'division', divisionOptions, 'All Divisions', handleDivisionChange)}
            {searchableField('Site', 'site', effectiveSiteOptions, 'All Sites', handleSiteChange)}
            {/* Warehouse — CR-4802: only when warehouse data is pulled in */}
            {fv.warehouse && selectField('Warehouse', 'warehouse', warehouseOptions, 'All Warehouses')}
          </FilterSection>

          <SectionDivider />

          {/* Supplier — Country, Group, Site, Supplier Code (cascades: Site/Country/Group → Supplier) */}
          <FilterSection title="Supplier" icon={Truck}>
            {selectField('Country', 'supplierCountry', toOptions(supplierCountries), 'All Countries', handleSupplierCountryChange)}
            {searchableField('Group', 'supplierGroup', supplierGroupOptions, 'All Supplier Groups', handleSupplierGroupChange)}
            {searchableField('Site', 'supplierId', effectiveSupplierOptions, 'All Supplier Sites')}
            {/* Supplier Code — CR-4802: only when the tenant holds supplier codes */}
            {fv.supplierCode && textField('Supplier Code', 'supplierCode', 'Enter supplier code')}
          </FilterSection>
        </>
      )}

      {/* CR-4802: the whole group is tenant-gated; drop the heading when nothing is shown. */}
      {fv.commodity && (
        <>
          <SectionDivider />

          {/* Classification — Commodity Group → Commodity Type */}
          <FilterSection title="Classification" icon={Tags}>
            {selectField('Commodity Group', 'commodityGroup', toOptions(filterOptions?.commodity_groups || []), 'All Commodity Groups', handleCommodityGroupChange)}
            {selectField('Commodity Type', 'commodityType', toOptions(effectiveCommodityTypes), 'All Commodity Types')}
          </FilterSection>
        </>
      )}

      <SectionDivider />

      {/* Forecast Details */}
      <FilterSection title="Forecast Details" icon={FileText}>
        {textField('Part Number', 'partNumber', 'Search part...')}
        {textField('PO Number', 'poNumber', 'Search PO...')}
        {isScheduleTenant && textField('Supplier Part No', 'supplierPartNumber', 'Supplier part...')}
        {selectField('Record Type', 'recordType', [
          { value: '2', label: 'Open PO' },
          { value: '1', label: 'Forecast' },
        ], 'All')}
      </FilterSection>
    </div>
  );
}
